import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  Activity,
  TimelineEvent,
  CreateActivityDto,
  UpdateActivityDto,
  QueryActivityDto,
} from '../types/activity';
import type { ApiResponse } from '../types/bewerber';
import { bewerberKeys } from './bewerber';
import { bedarfeKeys } from './bedarfe';

// Query keys
export const activityKeys = {
  all: ['activities'] as const,
  timelines: (bewerberId: string) => [...activityKeys.all, 'timeline', bewerberId] as const,
  timeline: (bewerberId: string, filters?: QueryActivityDto) =>
    filters
      ? ([...activityKeys.timelines(bewerberId), filters] as const)
      : activityKeys.timelines(bewerberId),
  lists: (bewerberId: string) => [...activityKeys.all, 'list', bewerberId] as const,
  list: (bewerberId: string, filters?: QueryActivityDto) =>
    filters
      ? ([...activityKeys.lists(bewerberId), filters] as const)
      : activityKeys.lists(bewerberId),
  details: () => [...activityKeys.all, 'detail'] as const,
  detail: (bewerberId: string, id: string) =>
    [...activityKeys.details(), bewerberId, id] as const,
};

// API functions
const fetchTimeline = async (
  bewerberId: string,
  query: QueryActivityDto = {},
): Promise<ApiResponse<TimelineEvent[]>> => {
  const params = new URLSearchParams();
  if (query.typ) params.append('typ', query.typ);
  if (query.startDate) params.append('startDate', query.startDate);
  if (query.endDate) params.append('endDate', query.endDate);
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());

  const response = await apiClient.get(
    `/bewerber/${bewerberId}/timeline?${params.toString()}`,
  );
  return response.data;
};

const fetchActivities = async (
  bewerberId: string,
  query: QueryActivityDto = {},
): Promise<ApiResponse<Activity[]>> => {
  const params = new URLSearchParams();
  if (query.typ) params.append('typ', query.typ);
  if (query.startDate) params.append('startDate', query.startDate);
  if (query.endDate) params.append('endDate', query.endDate);
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());

  const response = await apiClient.get(
    `/bewerber/${bewerberId}/activities?${params.toString()}`,
  );
  return response.data;
};

const fetchActivity = async (
  bewerberId: string,
  activityId: string,
): Promise<ApiResponse<Activity>> => {
  const response = await apiClient.get(
    `/bewerber/${bewerberId}/activities/${activityId}`,
  );
  return response.data;
};

const createActivity = async ({
  bewerberId,
  data,
}: {
  bewerberId: string;
  data: CreateActivityDto;
}): Promise<ApiResponse<Activity>> => {
  const response = await apiClient.post(
    `/bewerber/${bewerberId}/activities`,
    data,
  );
  return response.data;
};

const updateActivity = async ({
  bewerberId,
  activityId,
  data,
}: {
  bewerberId: string;
  activityId: string;
  data: UpdateActivityDto;
}): Promise<ApiResponse<Activity>> => {
  const response = await apiClient.patch(
    `/bewerber/${bewerberId}/activities/${activityId}`,
    data,
  );
  return response.data;
};

const deleteActivity = async ({
  bewerberId,
  activityId,
}: {
  bewerberId: string;
  activityId: string;
}): Promise<void> => {
  await apiClient.delete(`/bewerber/${bewerberId}/activities/${activityId}`);
};

// React Query Hooks

export function useBewerberTimeline(
  bewerberId: string,
  query: QueryActivityDto = {},
) {
  return useQuery({
    queryKey: activityKeys.timeline(bewerberId, query),
    queryFn: () => fetchTimeline(bewerberId, query),
    enabled: !!bewerberId,
  });
}

export function useBewerberActivities(
  bewerberId: string,
  query: QueryActivityDto = {},
) {
  return useQuery({
    queryKey: activityKeys.list(bewerberId, query),
    queryFn: () => fetchActivities(bewerberId, query),
    enabled: !!bewerberId,
  });
}

export function useActivity(bewerberId: string, activityId: string) {
  return useQuery({
    queryKey: activityKeys.detail(bewerberId, activityId),
    queryFn: () => fetchActivity(bewerberId, activityId),
    enabled: !!bewerberId && !!activityId,
  });
}

export function useCreateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createActivity,
    onSuccess: (_, variables) => {
      // Invalidate timeline and activities for this bewerber (use base keys for prefix matching)
      queryClient.invalidateQueries({
        queryKey: activityKeys.timelines(variables.bewerberId),
      });
      queryClient.invalidateQueries({
        queryKey: activityKeys.lists(variables.bewerberId),
      });
      // Also invalidate bewerber detail to refresh any counts
      queryClient.invalidateQueries({
        queryKey: bewerberKeys.detail(variables.bewerberId),
      });
    },
  });
}

export function useUpdateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateActivity,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: activityKeys.timelines(variables.bewerberId),
      });
      queryClient.invalidateQueries({
        queryKey: activityKeys.lists(variables.bewerberId),
      });
      queryClient.invalidateQueries({
        queryKey: activityKeys.detail(variables.bewerberId, variables.activityId),
      });
    },
  });
}

export function useDeleteActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteActivity,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: activityKeys.timelines(variables.bewerberId),
      });
      queryClient.invalidateQueries({
        queryKey: activityKeys.lists(variables.bewerberId),
      });
    },
  });
}

// ============================================
// BEDARF ACTIVITY KEYS & HOOKS
// ============================================

export const bedarfActivityKeys = {
  all: ['bedarf-activities'] as const,
  timelines: (bedarfId: string) => [...bedarfActivityKeys.all, 'timeline', bedarfId] as const,
  timeline: (bedarfId: string, filters?: QueryActivityDto) =>
    filters
      ? ([...bedarfActivityKeys.timelines(bedarfId), filters] as const)
      : bedarfActivityKeys.timelines(bedarfId),
  lists: (bedarfId: string) => [...bedarfActivityKeys.all, 'list', bedarfId] as const,
  list: (bedarfId: string, filters?: QueryActivityDto) =>
    filters
      ? ([...bedarfActivityKeys.lists(bedarfId), filters] as const)
      : bedarfActivityKeys.lists(bedarfId),
  details: () => [...bedarfActivityKeys.all, 'detail'] as const,
  detail: (bedarfId: string, id: string) =>
    [...bedarfActivityKeys.details(), bedarfId, id] as const,
};

// Bedarf API functions
const fetchBedarfTimeline = async (
  bedarfId: string,
  query: QueryActivityDto = {},
): Promise<ApiResponse<TimelineEvent[]>> => {
  const params = new URLSearchParams();
  if (query.typ) params.append('typ', query.typ);
  if (query.startDate) params.append('startDate', query.startDate);
  if (query.endDate) params.append('endDate', query.endDate);
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());

  const response = await apiClient.get(
    `/bedarfe/${bedarfId}/timeline?${params.toString()}`,
  );
  return response.data;
};

const fetchBedarfActivities = async (
  bedarfId: string,
  query: QueryActivityDto = {},
): Promise<ApiResponse<Activity[]>> => {
  const params = new URLSearchParams();
  if (query.typ) params.append('typ', query.typ);
  if (query.startDate) params.append('startDate', query.startDate);
  if (query.endDate) params.append('endDate', query.endDate);
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());

  const response = await apiClient.get(
    `/bedarfe/${bedarfId}/activities?${params.toString()}`,
  );
  return response.data;
};

const fetchBedarfActivity = async (
  bedarfId: string,
  activityId: string,
): Promise<ApiResponse<Activity>> => {
  const response = await apiClient.get(
    `/bedarfe/${bedarfId}/activities/${activityId}`,
  );
  return response.data;
};

const createBedarfActivity = async ({
  bedarfId,
  data,
}: {
  bedarfId: string;
  data: CreateActivityDto;
}): Promise<ApiResponse<Activity>> => {
  const response = await apiClient.post(
    `/bedarfe/${bedarfId}/activities`,
    data,
  );
  return response.data;
};

const updateBedarfActivity = async ({
  bedarfId,
  activityId,
  data,
}: {
  bedarfId: string;
  activityId: string;
  data: UpdateActivityDto;
}): Promise<ApiResponse<Activity>> => {
  const response = await apiClient.patch(
    `/bedarfe/${bedarfId}/activities/${activityId}`,
    data,
  );
  return response.data;
};

const deleteBedarfActivity = async ({
  bedarfId,
  activityId,
}: {
  bedarfId: string;
  activityId: string;
}): Promise<void> => {
  await apiClient.delete(`/bedarfe/${bedarfId}/activities/${activityId}`);
};

// Bedarf React Query Hooks

export function useBedarfTimeline(
  bedarfId: string,
  query: QueryActivityDto = {},
) {
  return useQuery({
    queryKey: bedarfActivityKeys.timeline(bedarfId, query),
    queryFn: () => fetchBedarfTimeline(bedarfId, query),
    enabled: !!bedarfId,
  });
}

export function useBedarfActivities(
  bedarfId: string,
  query: QueryActivityDto = {},
) {
  return useQuery({
    queryKey: bedarfActivityKeys.list(bedarfId, query),
    queryFn: () => fetchBedarfActivities(bedarfId, query),
    enabled: !!bedarfId,
  });
}

export function useBedarfActivity(bedarfId: string, activityId: string) {
  return useQuery({
    queryKey: bedarfActivityKeys.detail(bedarfId, activityId),
    queryFn: () => fetchBedarfActivity(bedarfId, activityId),
    enabled: !!bedarfId && !!activityId,
  });
}

export function useCreateBedarfActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createBedarfActivity,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: bedarfActivityKeys.timelines(variables.bedarfId),
      });
      queryClient.invalidateQueries({
        queryKey: bedarfActivityKeys.lists(variables.bedarfId),
      });
      queryClient.invalidateQueries({
        queryKey: bedarfeKeys.detail(variables.bedarfId),
      });
    },
  });
}

export function useUpdateBedarfActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBedarfActivity,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: bedarfActivityKeys.timelines(variables.bedarfId),
      });
      queryClient.invalidateQueries({
        queryKey: bedarfActivityKeys.lists(variables.bedarfId),
      });
      queryClient.invalidateQueries({
        queryKey: bedarfActivityKeys.detail(variables.bedarfId, variables.activityId),
      });
    },
  });
}

export function useDeleteBedarfActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBedarfActivity,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: bedarfActivityKeys.timelines(variables.bedarfId),
      });
      queryClient.invalidateQueries({
        queryKey: bedarfActivityKeys.lists(variables.bedarfId),
      });
    },
  });
}
